/**
 * MergeFieldComposer (AB#2464771)
 *
 * The single shared merge-field component. Bundles the MergeFieldEditor
 * (toolbar dropdown + `{` autocomplete + chips) together with the
 * TokenSidePanel (searchable field list) and wires the panel's `onInsert` to
 * the editor's imperative `insertToken()` so tokens land at the real cursor
 * position.
 *
 * Every screen that needs merge fields (Terms & Conditions, Notes, Section
 * editor, …) renders this so they all share an identical feature set. The panel
 * and the autocomplete are driven by the same scoped token list, so they show
 * identical fields, descriptions and format.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { MergeFieldEditor, type MergeFieldEditorHandle } from '@/components/ui/MergeFieldEditor';
import { TokenSidePanel, type TokenGroup } from '@/components/ui/TokenSidePanel';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  tokenDisplayName,
  type MergeTokenDef,
} from '@/services/mergeTokens';

/** Build TokenSidePanel groups from a scoped token list. */
export function buildTokenGroups(tokens: MergeTokenDef[]): TokenGroup[] {
  return CATEGORY_ORDER.map((cat) => ({
    label: CATEGORY_LABELS[cat],
    tokens: tokens
      .filter((t) => t.category === cat)
      .map((t) => ({
        token: t.token,
        label: tokenDisplayName(t),
        description: t.description,
      })),
  })).filter((g) => g.tokens.length > 0);
}

export interface MergeFieldComposerProps {
  /** HTML string content (may contain merge chip spans) */
  content: string;
  /** Called with updated HTML on every change */
  onChange: (html: string) => void;
  /** Scoped token list — e.g. `getMergeTokensForScope('terms')`. */
  tokens: MergeTokenDef[];
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  /** Class names for the outer flex row (must be a bounded/min-h-0 context). */
  className?: string;
  /** Class names for the editor column. */
  editorClassName?: string;
  /** Class names for the side panel. */
  panelClassName?: string;
  /** Hide the side panel (autocomplete + toolbar dropdown still available). */
  showPanel?: boolean;
  onFocus?: () => void;
}

export const MergeFieldComposer = React.forwardRef<
  MergeFieldEditorHandle,
  MergeFieldComposerProps
>(
  (
    {
      content,
      onChange,
      tokens,
      placeholder,
      maxLength,
      disabled,
      className,
      editorClassName,
      panelClassName,
      showPanel = true,
      onFocus,
    },
    ref,
  ) => {
    const editorRef = React.useRef<MergeFieldEditorHandle>(null);

    // Expose the editor handle to parents that also want to insert tokens.
    React.useImperativeHandle(ref, () => ({
      insertToken: (token: string) => editorRef.current?.insertToken(token),
      focus: () => editorRef.current?.focus(),
    }));

    const groups = React.useMemo(() => buildTokenGroups(tokens), [tokens]);

    return (
      <div className={cn('flex min-h-0', className)}>
        <div className={cn('flex-1 min-w-0', editorClassName)}>
          <MergeFieldEditor
            ref={editorRef}
            content={content}
            onChange={onChange}
            availableTokens={tokens}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            onFocus={onFocus}
          />
        </div>
        {showPanel && (
          <TokenSidePanel
            groups={groups}
            onInsert={(token) => editorRef.current?.insertToken(token)}
            className={panelClassName}
          />
        )}
      </div>
    );
  },
);

MergeFieldComposer.displayName = 'MergeFieldComposer';
